import { useEffect, useMemo, useState } from "react";
import { FileText, Mail, Trash2, RefreshCw, Plus } from "lucide-react";
import axios from "../../util/axiosInstance";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ReportGroupDetails from "./ReportGroupDetails.tsx";
import LastMonthReportDialog from "./LastMonthReportDialog.tsx";
import LastSixMonthReportDialog from "./LastSixMonthReportDialog.tsx";
import AddReportGroup from "./AddReportGroup.tsx";
import ConfirmDialog from "../../../components/ConfirmDialog.jsx";
import { getUIDescription } from "../../../components/SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice.jsx";
type ReportGroup = {
  id: string;
  name: string;
  description: string;
  date_created: string;
  devices: number;
};

export default function ReportGroups() {
  const navigate = useNavigate();
  const [loadingReportGroups, setLoadingReportGroups] = useState(false);
  const [reportGroups, setReportGroups] = useState<ReportGroup[]>([]);
  const [errorFetching, setErrorFetching] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<ReportGroup | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState("Name");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [lastMonthReportDialogOpen, setLastMonthReportDialogOpen] =
    useState(false);
  const [lastSixMonthReportDialogOpen, setLastSixMonthReportDialogOpen] =
    useState(false);
  const [lastYearReportDialogOpen, setLastYearReportDialogOpen] =
    useState(false);
  const [selectedGroup, setSelectedGroup] = useState<ReportGroup | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedDetailsGroup, setSelectedDetailsGroup] =
    useState<ReportGroup | null>(null);
  const [loading, setLoading] = useState(false);

  const addReportGroup = () => setShowAddForm(true);

  const goToUnitsSubs = (grp: ReportGroup) => {
    setSelectedDetailsGroup(grp);
    setShowDetails(true);
  };

  const handleDeleteClick = (grp: ReportGroup) => {
    setGroupToDelete(grp);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!groupToDelete) return;
    try {
      setConfirmOpen(false);
      const response = await axios.delete(
        `${process.env.REACT_APP_API_GATEAWAY}/groups/${groupToDelete.id}`,
        {
          data: { UI_Description: `${getUIDescription()}\n Delete Report Group`,  },
        }
      );
      if (response.status >= 200 && response.status < 300) {
        toast.success("Report Group Successfully Deleted");
        await fetchReportGroups();
        setGroupToDelete(null);
      } else {
        toast.error("Failed to delete report group");
      }
    } catch (err) {
      console.error("❌ Error deleting report group:", err);
      toast.error("Failed to delete report group");
    }
  };

  const fetchReportGroups = async () => {
    try {
      setLoadingReportGroups(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/reportGroups`
      );
      setReportGroups(response.data || []);
    } catch (err) {
      console.error("Failed to fetch report groups:", err);
      setErrorFetching("Error fetching report groups");
    } finally {
      setLoadingReportGroups(false);
    }
  };

  useEffect(() => {
    fetchReportGroups();
  }, []);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    const sorted = [...reportGroups];
    sorted.sort((a, b) => {
      let aValue: any =
        a[orderBy.toLowerCase().replace(/\s+/g, "_") as keyof ReportGroup];
      let bValue: any =
        b[orderBy.toLowerCase().replace(/\s+/g, "_") as keyof ReportGroup];

      if (orderBy === "Name") {
        aValue = a.name || "";
        bValue = b.name || "";
      } else if (orderBy === "Date Created") {
        aValue = new Date(a.date_created);
        bValue = new Date(b.date_created);
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [reportGroups, order, orderBy]);

  const handleOpenLastReportDialog = async (grp: ReportGroup) => {
    setSelectedGroup(grp);

    setLastMonthReportDialogOpen(true);
  };

  const handleOpenLastSixReportDialog = async (grp: ReportGroup) => {
    setSelectedGroup(grp);
    setLastSixMonthReportDialogOpen(true);
  };

  const handleOpenLastYearReportDialog = async (grp: ReportGroup) => {
    setSelectedGroup(grp);
    setLastYearReportDialogOpen(true);
  };

  const handleSendEmail = async (grp: ReportGroup) => {
    if (!grp) return;
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/sendReports/monthlyReport/reportGroup/${grp.id}`,
        {
          year: new Date().getFullYear(),
          month: new Date().getMonth(),
          UI_Description: `${getUIDescription()}\n Send Email Monthly report`,  
        }
      );
      if (response.data.success === true) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      console.error("❌ Error sending email:", err);
      toast.error("Failed to send email");
    } finally {
      setLoading(false);
    }
  };
  const filteredData = useMemo(() => {
    return sortedData.filter(
      (row) =>
        row.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        row.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        new Date(row.date_created)
          .toLocaleString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
    );
  }, [sortedData, searchQuery]);

  const paginatedData = useMemo(() => {
    return filteredData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredData, page, rowsPerPage]);

  if (showDetails) {
    return (
      <ReportGroupDetails
        group={selectedDetailsGroup}
        onBack={() => {
          setShowDetails(false);
          setSelectedDetailsGroup(null);
        }}
      />
      //<div>ReportGroupDetails</div>
    );
  }

  //   if (loadingReportGroups) {
  //     return (
  //       <div className="w-full flex justify-center items-center min-h-[200px]">
  //         <div className="animate-spin h-12 w-12 border-4 border-blue-400 border-t-transparent rounded-full" />
  //       </div>
  //     );
  //   }

  if (errorFetching) {
    return (
      <div className="text-red-500 font-bold text-center py-4">
        {errorFetching}
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col rounded-lg bg-slate-900">
      <div className="flex justify-between items-center flex-wrap gap-2 px-4 mt-2 mb-4">
        <h1 className="text-xl font-bold mt-5">Report Groups</h1>
      </div>

      {loadingReportGroups ? (
        <div className="w-full flex justify-center items-center min-h-[200px]">
          <div className="animate-spin h-12 w-12 border-4 border-blue-400 border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-4 px-4">
            <div className="w-full md:w-auto">
              <input
                type="text"
                placeholder="Search Report Group..."
                className="w-full md:w-[300px] px-2 py-1 rounded bg-slate-800 text-white border border-slate-600"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={fetchReportGroups}
                className="px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
              >
                <RefreshCw size={16} />
                Refresh
              </button>

              <button
                onClick={addReportGroup}
                className="px-2 py-1 rounded border border-blue-600 text-blue-400 hover:bg-blue-600 hover:text-white flex items-center gap-2"
              >
                <Plus size={16} />
                Add Report Group
              </button>
            </div>
          </div>

          <div className="overflow-auto max-h-[63vh] px-2 mt-5 rounded-lg">
            <div className="rounded-xl overflow-hidden border border-gray-700">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-700 text-white sticky top-0">
                  <tr className="border-b border-slate-700 bg-slate-800">
                    {[
                      "Name",
                      "Description",
                      "Date Created",
                      "Last Month Report",
                      "Last Six Months",
                      "Last Year",
                      "Email Month report",
                      "Delete",
                    ].map((col) => (
                      <th
                        key={col}
                        className="p-2 cursor-pointer select-none font-semibold"
                        onClick={() => handleRequestSort(col)}
                      >
                        <div className="flex items-center justify-center w-full">
                          <span className="flex items-center gap-1">{col}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {paginatedData.length ? (
                    paginatedData.map((grp, index) => (
                      //   {filteredData.length > 0 ? (
                      //     filteredData.map((grp, index) => (
                      <tr
                        key={grp.id}
                        className={
                          index % 2 === 0
                            ? "bg-gray-800 hover:bg-gray-700"
                            : "bg-gray-700 hover:bg-gray-600"
                        }
                      >
                        <td className="px-2 py-1">
                          <div className="flex items-center justify-center w-full">
                            <button
                              onClick={() => goToUnitsSubs(grp)}
                              className="text-blue-400 hover:text-blue-300 hover:underline font-medium"
                            >
                              {grp.name}
                            </button>
                          </div>
                        </td>

                        <td className="px-2 py-1">
                          {" "}
                          <div className="flex items-center justify-center w-full">
                            {grp.description}
                          </div>
                        </td>

                        <td className="px-2 py-1">
                          <div className="flex items-center justify-center w-full">
                            {new Date(grp.date_created).toLocaleString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                              }
                            )}
                          </div>
                        </td>

                        <td className="px-2 py-1 text-center">
                          <button
                            onClick={() => handleOpenLastReportDialog(grp)}
                            className="p-2 rounded hover:bg-blue-600 text-blue-400 hover:text-white"
                            title="View Last Month Report"
                          >
                            <FileText size={20} />
                          </button>
                        </td>

                        <td className="px-2 py-1 text-center">
                          <button
                            onClick={() => handleOpenLastSixReportDialog(grp)}
                            className="p-2 rounded hover:bg-green-600 text-green-400 hover:text-white"
                            title="View Last Six Months Reports"
                          >
                            <FileText size={20} />
                          </button>
                        </td>

                        <td className="px-2 py-1 text-center">
                          <button
                            onClick={() => handleOpenLastYearReportDialog(grp)}
                            className="p-2 rounded hover:bg-yellow-600 text-yellow-400 hover:text-white"
                            title="View Last Year Report"
                          >
                            <FileText size={20} />
                          </button>
                        </td>

                        <td className="px-2 py-1 text-center">
                          <button
                            onClick={() => handleSendEmail(grp)}
                            className="p-2 rounded hover:bg-gray-600 text-gray-400 hover:text-white"
                            title="Send Monthly Report"
                          >
                            <Mail size={20} />
                          </button>
                        </td>

                        <td className="px-2 py-1">
                          <div className="flex items-center justify-center w-full">
                            <button
                              onClick={() => handleDeleteClick(grp)}
                              className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 text-sm flex items-center gap-1"
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center py-4">
                        No report groups found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex justify-end items-center py-2 px-3 text-white gap-2">
            <div className="w-32 text-right">Rows:</div>
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              className="bg-gray-800 border border-gray-600 rounded px-1 py-1"
            >
              {[5, 10, 25, 50, 100].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
          </div>

          <AddReportGroup
            open={showAddForm}
            onClose={() => setShowAddForm(false)}
            onAdded={fetchReportGroups}
          />
          <LastMonthReportDialog
            open={lastMonthReportDialogOpen}
            onClose={() => setLastMonthReportDialogOpen(false)}
            groupId={selectedGroup?.id}
            groupName={selectedGroup?.name}
          />
          <LastSixMonthReportDialog
            open={lastSixMonthReportDialogOpen}
            onClose={() => setLastSixMonthReportDialogOpen(false)}
            groupId={selectedGroup?.id}
            groupName={selectedGroup?.name}
            title={"Last 6 Months Report"}
            mOffset={6}
          />
          <LastSixMonthReportDialog
            open={lastYearReportDialogOpen}
            onClose={() => setLastYearReportDialogOpen(false)}
            groupId={selectedGroup?.id}
            groupName={selectedGroup?.name}
            title={"Last Year Report"}
            mOffset={13}
          />
          <ConfirmDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={handleConfirmDelete}
          />
        </>
      )}
    </div>
  );
}
